import { useTranslation } from "react-i18next";
import { useState, useEffect, useMemo } from "react";
import { Form, InputNumber } from "antd";
import CustomModal from "../../../components/Common/CustomModal";
import { FeeCreatePayload } from "../../../types/feeTypes";
import { StudentType } from "types/studentTypes";
import { FeeType } from "types/feeTypeTypes";
import { formatMoney } from "helpers/numberHelper";
import CommonSelect, { Option as SelectOption } from "../../../components/Common/CommonSelect";
import CommonMultiSelect from "components/Common/CommonMultiSelect";
import LocalizedDatePicker from "components/Common/ui/LocalizedDatePicker";
import { getClassLevelLabelFromCode } from "utils/schoolUtils";

interface AddEditFeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: FeeCreatePayload) => void;
  students: StudentType[];
  feeTypes: FeeType[];
  selectedStudent: string;
}

const AddEditFeeModal: React.FC<AddEditFeeModalProps> = ({
  isOpen,
  onClose,
  onSave,
  students,
  feeTypes,
  selectedStudent,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const [selectedFeeType, setSelectedFeeType] = useState<FeeType | null>(null);
  const [selectedClassLevelId, setSelectedClassLevelId] = useState<string | null>(null);

  // ---------- Helpers ----------
  const findStudentById = (id?: string | null) =>
    id ? students.find((s) => s.id === id) : undefined;

  const monthsOptions = useMemo(
    () => [
      { value: 0, label: t("list_of_months.January") },
      { value: 1, label: t("list_of_months.February") },
      { value: 2, label: t("list_of_months.March") },
      { value: 3, label: t("list_of_months.April") },
      { value: 4, label: t("list_of_months.May") },
      { value: 5, label: t("list_of_months.June") },
      { value: 6, label: t("list_of_months.July") },
      { value: 7, label: t("list_of_months.August") },
      { value: 8, label: t("list_of_months.September") },
      { value: 9, label: t("list_of_months.October") },
      { value: 10, label: t("list_of_months.November") },
      { value: 11, label: t("list_of_months.December") },
    ],
    [t]
  );

  const computeAmount = (feeType: FeeType | null, classLevelId?: string | null) => {
    if (!feeType) return undefined;

    if (feeType.pricingModel === "unique_payment") {
      return Number(feeType.baseAmount ?? 0);
    }

    if (feeType.pricingModel === "level_based_payment") {
      if (classLevelId) {
        const found = feeType.feePricing?.find((p) => p.classLevelId === classLevelId);
        if (found) return Number(found.amount ?? 0);
        return undefined; // user must choose feePricing
      }
      return undefined;
    }

    return undefined;
  };

  // ---------- Handlers ----------
  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const { feePricingId, dueDate, dueMonths, ...rest } = values;

      const payload: FeeCreatePayload = {
        ...rest,
        ...(dueDate ? { dueDate: dueDate.format("MM-DD-YYYY") } : {}),
        ...(Array.isArray(dueMonths) ? { dueMonths } : {}),
      };

      await onSave(payload);
      onClose();
      form.resetFields();
      setSelectedFeeType(null);
      setSelectedClassLevelId(null);
    } catch (err) {
      console.error("Error saving fee:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStudentChange = (option: SelectOption | null) => {
    const newStudentId = option?.value || null;
    form.setFieldValue("studentId", newStudentId);

    const stu = findStudentById(newStudentId);
    const newClassLevelId = stu?.class?.classLevel?.id ?? null;
    setSelectedClassLevelId(newClassLevelId);

    const newAmount = computeAmount(selectedFeeType, newClassLevelId);
    form.setFieldsValue({ amount: newAmount });
  };

  const handleFeeTypeChange = (option: SelectOption | null) => {
    const feeTypeId = option?.value || null;
    const newFeeType = feeTypes.find((ft) => ft.id === feeTypeId) || null;
    setSelectedFeeType(newFeeType);
    form.setFieldValue("feeTypeId", feeTypeId);
    form.setFieldsValue({ feePricingId: undefined }); // clear if was set
    const newAmount = computeAmount(newFeeType, selectedClassLevelId);
    form.setFieldsValue({ amount: newAmount });
  };

  const handleFeePricingChange = (option: SelectOption | null) => {
    if (!selectedFeeType) return;
    const selectedPricing = selectedFeeType.feePricing?.find((p) => p.id === option?.value);
    form.setFieldsValue({
      feePricingId: option?.value || null,
      amount: Number(selectedPricing?.amount ?? 0),
    });
  };

  // ---------- Effects ----------
  useEffect(() => {
    if (!isOpen) return;

    form.resetFields();
    setSelectedFeeType(null);

    if (selectedStudent) {
      form.setFieldValue("studentId", selectedStudent);
      const stu = findStudentById(selectedStudent);
      const lvl = stu?.class?.classLevel?.id ?? null;
      setSelectedClassLevelId(lvl);
    } else {
      setSelectedClassLevelId(null);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const amount = computeAmount(selectedFeeType, selectedClassLevelId);
    if (amount !== undefined) form.setFieldsValue({ amount });
  }, [selectedFeeType, selectedClassLevelId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Options ----------
  const studentOptions: SelectOption[] = useMemo(
    () =>
      students.map((s) => ({
        value: s.id,
        label: `${s.user.firstName} ${s.user.lastName}`,
      })),
    [students]
  );

  const feeTypeOptions: SelectOption[] = useMemo(
    () => feeTypes.map((ft) => ({ value: ft.id, label: ft.name })),
    [feeTypes]
  );

  const feePricingOptions: SelectOption[] = useMemo(() => {
    if (!selectedFeeType?.feePricing) return [];
    return selectedFeeType.feePricing.map((p) => ({
      value: p.id,
      label: `${getClassLevelLabelFromCode(p.classLevel?.labelKey ?? "")} - ${formatMoney(Number(p.amount ?? 0))}`,
    }));
  }, [selectedFeeType]);

  return (
    <CustomModal
      title={t("add_fees")}
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      loading={loading}
      saveText={t("add")}
    >
      <Form form={form} layout="vertical">
        {/* Student (hidden if provided from props) */}
        {selectedStudent ? (
          <Form.Item name="studentId" hidden initialValue={selectedStudent}>
            <input type="hidden" />
          </Form.Item>
        ) : (
          <Form.Item
            label={t("student")}
            name="studentId"
            rules={[{ required: true, message: t("validation.please_select_student") }]}
          >
            <CommonSelect
              options={studentOptions}
              minSearchChars={3}              // ⬅️ gated search
              isSearchable                     // keep default true explicit
              onChange={handleStudentChange}
            />
          </Form.Item>
        )}

        {/* Fee type */}
        <Form.Item
          label={t("fee_type")}
          name="feeTypeId"
          rules={[{ required: true, message: t("validation.please_select_fee_type") }]}
        >
          <CommonSelect
            options={feeTypeOptions}
            onChange={handleFeeTypeChange}
          />
        </Form.Item>

        {/* Pricing (only when level unknown) */}
        {selectedFeeType?.pricingModel === "level_based_payment" && !selectedClassLevelId && (
          <Form.Item
            label={t("pricing_type")}
            name="feePricingId"
            rules={[{ required: true, message: t("validation.please_select_fee_pricing") }]}
          >
            <CommonSelect
              options={feePricingOptions}
              onChange={handleFeePricingChange}
            />
          </Form.Item>
        )}

        {/* Amount */}
        <Form.Item
          label={t("amount")}
          name="amount"
          rules={[{ required: true, message: t("validation.please_enter_amount") }]}
        >
          <InputNumber
            className="w-100"
            placeholder={t("amount")}
            min={0}
            precision={2}
            addonAfter="MAD"
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          />
        </Form.Item>

        {/* Unique vs recurring */}
        {selectedFeeType?.frequency === "unique_payment" ? (
          <Form.Item
            label={t("due_date")}
            name="dueDate"
            rules={[{ required: true, message: t("validation.please_select_due_date") }]}
          >
            <LocalizedDatePicker className="w-100" />
          </Form.Item>
        ) : (
          <Form.Item
            label={t("select_months")}
            name="dueMonths"
            rules={[{ required: true, message: t("validation.please_select_months") }]}
          >
            <CommonMultiSelect
              options={monthsOptions}
              placeholder={t("select_months")}
              value={form.getFieldValue("dueMonths")}
              onChange={(value: number[]) => form.setFieldValue("dueMonths", value)}
            />
          </Form.Item>
        )}
      </Form>
    </CustomModal>
  );
};

export default AddEditFeeModal;
